import { ref, computed, watch } from '@vue/composition-api';
import { omitBy, isNil } from 'lodash';
export const useTableQueryParams = (ctx, config = {}) => {
    const { defaultSort, fieldMap = {}, defaultPerPage = 25, queryPrefix = '', filterParams = {}, staticParams = {}, } = config;
    const { $route, $router } = ctx.root;
    const getQueryParamName = (param) => `${queryPrefix}${param}`;
    const page = ref(Number($route.query[getQueryParamName('page')]) || 1);
    const perPage = ref(Number($route.query[getQueryParamName('perPage')]) || defaultPerPage);
    const sort = ref($route.query[getQueryParamName('sort')] || defaultSort);
    const initializeFilters = () => {
        const localFilters = {};
        Object.keys(filterParams).forEach((filterKey) => {
            localFilters[filterKey] = { filter: null };
        });
        return localFilters;
    };
    const filters = ref(initializeFilters());
    const query = computed(() => {
        const baseQuery = {
            page: page.value,
            perPage: perPage.value,
        };
        if (sort.value) {
            baseQuery.sort = sort.value;
        }
        Object.entries(staticParams).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                baseQuery[key] = value;
            }
        });
        Object.entries(filterParams).forEach(([filterKey]) => {
            const filterValue = filters.value[filterKey]?.filter;
            if (filterValue !== null &&
                filterValue !== undefined &&
                filterValue !== '') {
                baseQuery[filterKey] = filterValue;
            }
        });
        const { page: _, perPage: __, ...cleanableQuery } = baseQuery;
        const cleanQuery = omitBy(cleanableQuery, (value) => isNil(value) || value === '');
        return {
            ...cleanQuery,
            page: page.value,
            perPage: perPage.value,
        };
    });
    const updateUrlParams = () => {
        const currentQuery = $route.query;
        const queryParams = {};
        Object.entries(currentQuery).forEach(([key, value]) => {
            if (typeof value === 'string') {
                queryParams[key] = value;
            }
            else if (Array.isArray(value) && value.length > 0) {
                queryParams[key] = value[0] || '';
            }
        });
        if (page.value > 1) {
            queryParams[getQueryParamName('page')] = page.value.toString();
        }
        else {
            delete queryParams[getQueryParamName('page')];
        }
        queryParams[getQueryParamName('perPage')] = perPage.value.toString();
        delete queryParams[getQueryParamName('sort')];
        delete queryParams[getQueryParamName('sortBy')];
        if (sort.value) {
            queryParams[getQueryParamName('sort')] = sort.value;
        }
        const cleanQuery = Object.entries(queryParams).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {});
        try {
            $router.replace({ query: cleanQuery });
        }
        catch (error) {
            console.error('updateUrlParams', error);
        }
    };
    const handleSort = (field, direction) => {
        const sortField = fieldMap[field] || field;
        const prefix = direction === 'asc' ? '' : '-';
        sort.value = `${prefix}${sortField}`;
        page.value = 1;
    };
    const resetSort = () => {
        sort.value = undefined;
        page.value = 1;
    };
    const handleFilterChange = (filterModel) => {
        Object.keys(filters.value).forEach((filterKey) => {
            const mappedFieldName = Object.keys(fieldMap).find((key) => fieldMap[key] === filterKey);
            const agFilter = (filterModel[filterKey] ||
                (mappedFieldName && filterModel[mappedFieldName]));
            if (agFilter && agFilter.filter !== undefined) {
                filters.value[filterKey] = { filter: agFilter.filter };
            }
            else if (agFilter && agFilter.values && agFilter.values.length > 0) {
                filters.value[filterKey] = { filter: agFilter.values[0] };
            }
            else {
                filters.value[filterKey] = { filter: null };
            }
        });
        page.value = 1;
    };
    const setFilter = (filterKey, value) => {
        if (filters.value[filterKey]) {
            filters.value[filterKey] = { filter: value };
            page.value = 1;
        }
    };
    const clearFilters = () => {
        Object.keys(filters.value).forEach((filterKey) => {
            filters.value[filterKey] = { filter: null };
        });
        page.value = 1;
    };
    watch([page, perPage, sort], () => {
        updateUrlParams();
    });
    return {
        page,
        perPage,
        sort,
        filters,
        query,
        handleSort,
        resetSort,
        handleFilterChange,
        setFilter,
        clearFilters,
        updateUrlParams,
    };
};
